import { ExclamationCircleFilled } from '@ant-design/icons';
import {
  Button,
  DatePicker,
  Flex,
  Form,
  message,
  Popover,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import useUpdateApdMutation, {
  UpdateApdMutationPayload,
} from 'screens/awd-dashboard/hooks/mutations/useUpdateApdMutation';
import { SeasonParticipant } from 'types/common';

interface EditFarmerApdFormProps {
  participant: SeasonParticipant;
  taskAssignmentId: string;
  onEditSuccess?: () => void;
}

const EditFarmerApdForm = ({
  participant,
  taskAssignmentId,
  onEditSuccess: onSuccess,
}: EditFarmerApdFormProps) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();

  const updateApdMutation = useUpdateApdMutation();

  const handleFormSubmit = (values: {
    actualPlantingDate: dayjs.Dayjs | null;
  }) => {
    const payload: UpdateApdMutationPayload = {
      taskAssignment: {
        id: taskAssignmentId,
      },
      participants: [
        {
          id: participant.id,
          actualPlantingDate: values.actualPlantingDate?.toISOString()!,
        },
      ],
    };
    updateApdMutation.mutateAsync(payload, {
      onSuccess: () => {
        message.success(t('dashboard.editApdSuccess'));
        onSuccess?.();
      },
      onError: (error) => {
        message.error(error?.message || t('common.somethingWrong'));
      },
    });
  };

  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={{
        actualPlantingDate: participant?.actualPlantingDate
          ? dayjs(participant.actualPlantingDate)
          : null,
      }}
      onFinish={handleFormSubmit}
      requiredMark={false}
    >
      <Form.Item
        className="w-100"
        name="actualPlantingDate"
        label={t('dashboard.actualPlantingDate')}
        rules={[{ required: true, message: 'Please select a date' }]}
      >
        <DatePicker
          format="MMM DD, YYYY"
          className="w-full mb-1"
          disabledDate={(current) => current && current > dayjs().endOf('day')}
        />
      </Form.Item>

      <Popover
        styles={{ root: { maxWidth: 350 } }}
        content={
          <Flex vertical gap={8}>
            <Typography.Text>{t('dashboard.editApdContent')}</Typography.Text>

            <Flex justify="end" gap={8}>
              <Button type="default" htmlType="reset" className="w-fit mt-1">
                {t('common.cancel')}
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                className="w-fit mt-1"
                onClick={() => form.submit()}
                disabled={updateApdMutation.isPending}
              >
                {updateApdMutation?.isPending
                  ? t('common.saving')
                  : t('common.yesSave')}
              </Button>
            </Flex>
          </Flex>
        }
        title={
          <Flex gap={8} align="center">
            <ExclamationCircleFilled
              style={{ color: '#FAAD14', fontSize: 18 }}
            />
            <Typography.Text>{t('dashboard.editApd')}</Typography.Text>
          </Flex>
        }
        trigger="click"
        placement="bottomLeft"
      >
        <Button
          type="primary"
          htmlType="button"
          className="w-fit mt-1"
          disabled={updateApdMutation.isPending}
        >
          {t('common.save')}
        </Button>
      </Popover>
    </Form>
  );
};

export default EditFarmerApdForm;
